<template>
  <el-dialog
    :visible.sync="currentlyVisible"
    title="Create Spot Survey"
    :before-close="handleCancel"
  >
    <p>Create a new spot survey by asking questions, and providing the possible choices a user can select from</p>

    <el-form label-width="120px" :model="surveyForm" :rules="rules" ref="surveyForm">
      <el-form-item label="Survey title" prop="title">
        <el-input v-model="surveyForm.title"/>
      </el-form-item>

      <el-form-item label="Campaign" prop="campaignId">
        <el-select
          v-model="surveyForm.campaignId"
          filterable
          placeholder="Select"
          style="width: 100%"
        >
          <el-option
            v-for="item in activeCampaigns"
            :key="item.id"
            :label="item.title"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <div class="row">
        <div class="col-lg-6">
          <el-form-item label="Start date" prop="startDate">
            <el-date-picker type="date" v-model="surveyForm.startDate" style="width: 100%"/>
          </el-form-item>
        </div>
        <div class="col-lg-6">
          <el-form-item label="End date" prop="endDate">
            <el-date-picker type="date" v-model="surveyForm.endDate" style="width: 100%"/>
          </el-form-item>
        </div>
      </div>

      <el-form-item label="Redirect URL" prop="redirectUrl">
        <el-input v-model="surveyForm.redirectUrl" placeholder="https://"/>
        <p
          class="help-text"
        >After the listener has completed the survey successfully, where do we want to redirect them?</p>
      </el-form-item>

      <el-form-item label="Logo URL" prop="logoUrl">
        <el-input v-model="surveyForm.logoUrl" placeholder="https://"/>
        <p
          class="help-text"
        >Display this logo image on the spot survey form presented to the listener.</p>
      </el-form-item>

      <div
        class="question-block"
        v-for="(question, index) in surveyForm.questions"
        :key="question.id"
      >
        <el-form-item
          label="Question"
          :prop="`questions.${index}.title`"
          :rules="{ required: true, message: 'Please provide a question', trigger: 'blur' }"
        >
          <el-input type="textarea" v-model="question.title"/>
        </el-form-item>
        <div class="row">
          <div class="col-lg-6">
            <el-form-item label="Choice">
              <el-input v-model="question.newChoice"/>
              <el-button
                type="info"
                size="mini"
                class="add-btn pull-right"
                @click="handleAddChoice(question, index)"
                round
              >
                <i class="fa fa-fw fa-plus"/> Add choice
              </el-button>
              <div class="clearfix"></div>
            </el-form-item>
          </div>
          <div class="col-lg-6">
            <div v-if="question.choices.length === 0" class="el-form-item__error">
              <em>No choices have been added for this question yet!</em>
            </div>
            <ul class="todo-list choices">
              <li v-for="(choice, choiceIndex) in question.choices" :key="choice.id">
                {{ choice.title }}
                <span class="pull-right">
                  <a @click="handleRemoveChoice(index, choiceIndex)">
                    <i class="fa fa-fw fa-trash"></i>
                  </a>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <el-button
          type="success"
          size="mini"
          v-if="surveyForm.questions.length < 2"
          @click="handleAddQuestion"
          class="add-btn pull-right"
          round
        >
          <i class="fa fa-fw fa-plus"/>Add another question
        </el-button>
        <el-button
          type="danger"
          size="mini"
          v-if="index > 0"
          @click="handleRemoveQuestion(index)"
          class="add-btn pull-right"
          round
        >
          <i class="fa fa-fw fa-trash"/>Remove question
        </el-button>
        <div class="clearfix"></div>
      </div>

      <div class="question-block">
        <el-form-item label="Profiles">
          <el-transfer
            v-model="surveyForm.profiles"
            :data="demographicsTransfer"
            :titles="['Available', 'Enabled']"
            :button-texts="['Disable', 'Enable']"
          />
        </el-form-item>
      </div>
    </el-form>

    <div class="alert alert-warning">
      <p>
        <strong>Note:</strong> Due to the way we currently process survey data,
        once you click
        <em>Create</em>, you will not be able to modify this spot survey again.
        Please ensure that all information is correct before proceeding.
      </p>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">Cancel</el-button>
      <el-button type="primary" @click="handleCreateSpotSurvey">Create</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'SpotSurveyCreateDialog',
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    surveyForm: {
      title: null,
      campaignId: null,
      startDate: null,
      endDate: null,
      redirectUrl: null,
      logoUrl: null,
      questions: [{ title: '', choices: [], newChoice: '' }],
      profiles: [],
    },
    rules: {
      title: [{ required: true, message: 'Please provide a title', trigger: 'blur' }],
      campaignId: [
        { required: true, message: 'Please select an active campaign', trigger: 'change' },
      ],
      startDate: [
        { type: 'date', required: true, message: 'Please pick a starting date', trigger: 'change' },
      ],
      endDate: [
        { type: 'date', required: true, message: 'Please pick an ending date', trigger: 'change' },
      ],
      redirectUrl: [
        {
          message: 'Please provide a valid url',
          trigger: 'blur',
          pattern: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
        },
      ],
      logoUrl: [
        {
          message: 'Please provide a valid url',
          trigger: 'blur',
          pattern: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
        },
      ],
    },
  }),
  computed: {
    ...mapState('survey', ['demographics']),
    ...mapGetters('campaign', ['activeCampaigns']),
    demographicsTransfer() {
      return this.demographics.map(d => ({ key: d.id, label: d.title }));
    },
    currentlyVisible: {
      get() {
        return this.isVisible;
      },

      set(newValue) {
        this.$emit('onVisibilityChange', newValue);
        this.$refs.surveyForm.resetFields();
        this.surveyForm.questions = [{ title: '', choices: [], newChoice: '' }];
        this.surveyForm.profiles = [];
      },
    },
  },
  methods: {
    handleAddChoice(question, index) {
      if (question.newChoice.length > 0) {
        question.choices.push({ title: question.newChoice });
        this.surveyForm.questions[index].newChoice = '';
      }
    },
    handleAddQuestion() {
      this.surveyForm.questions.push({ title: '', choices: [], newChoice: '' });
    },
    handleCancel() {
      this.currentlyVisible = false;
    },
    handleCreateSpotSurvey() {
      // Work with a copy of our form data
      const formData = Object.assign({}, this.surveyForm);
      this.$refs.surveyForm.validate(valid => {
        if (valid && this.choicesValid()) {
          this.$store.dispatch('survey/createSpotSurvey', formData);
        }
      });
      // Silly thing!
      window.setTimeout(() => {
        this.currentlyVisible = false;
      }, 0);
    },
    handleRemoveChoice(questionIndex, choiceIndex) {
      this.surveyForm.questions[questionIndex].choices.splice(choiceIndex, 1);
    },
    handleRemoveQuestion(questionIndex) {
      this.surveyForm.questions.splice(questionIndex, 1);
    },

    choicesValid() {
      let isValid = true;
      this.surveyForm.questions.forEach(question => {
        if (!question.choices.length) {
          isValid = false;
        }
      });
      return isValid;
    },
  },
};
</script>

<style lang="scss" scoped>
.question-block {
  border-top: 1px solid #eee;
  padding-top: 25px;
  margin-top: 15px;
}
.help-text {
  margin-top: -5px;
  font-size: 0.9em;
}
.choices {
  font-size: 1.2em;
}
.add-btn {
  margin-top: 6px;
  margin-bottom: 6px;
}
</style>



// WEBPACK FOOTER //
// SpotSurveyCreateDialog.vue?49ac1a1f